import { Card, Button, Descriptions, Modal, Input, message } from 'antd';
import { EyeOutlined } from '@ant-design/icons';
import { useState } from 'react';
import type { TenantOrder } from '@/mocks/orders';
import { useRevealOrderSensitive } from '@/hooks/useOrderMutations';

interface InvoiceCardProps {
  order: TenantOrder;
  onEdit?: () => void;
}

export function InvoiceCard({ order, onEdit }: InvoiceCardProps) {
  const { buyer_invoice_info } = order;

  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [isCccdVisible, setIsCccdVisible] = useState(false);

  const revealMutation = useRevealOrderSensitive();
  const [decryptedCccd, setDecryptedCccd] = useState<string | null>(null);

  const handleViewCccd = () => {
    setIsVerifyModalOpen(true);
  };

  const handleVerifyPassword = async () => {
    if (!password.trim()) {
      message.error('Vui lòng nhập mật khẩu');
      return;
    }
    try {
      const res = await revealMutation.mutateAsync({ id: order._id, password });
      setIsVerifyModalOpen(false);
      setDecryptedCccd((res as any).buyer_cccd || res.invoice_buyer_cccd || buyer_invoice_info?.buyer_cccd || '');
      setIsCccdVisible(true);
      setPassword('');
      message.success('Giải mã thành công');
    } catch (err: any) {
      message.error(err?.response?.data?.message || 'Mật khẩu không đúng hoặc có lỗi xảy ra');
    }
  };

  const renderCccd = () => {
    if (!buyer_invoice_info?.buyer_cccd) return '---';
    if (isCccdVisible && decryptedCccd) {
      return decryptedCccd;
    }
    
    // Nếu là ciphertext (thường bắt đầu bằng v1:) thì mask hết, nếu là plaintext 12 số thì mask giữa
    if (buyer_invoice_info.buyer_cccd.startsWith('v1:')) {
      return (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
          <span>********</span>
          <EyeOutlined style={{ cursor: 'pointer', color: '#0958d9' }} onClick={handleViewCccd} />
        </span>
      );
    }
    
    const cccd = buyer_invoice_info.buyer_cccd;
    const masked = cccd.length >= 10 ? `${cccd.slice(0, 3)}***${cccd.slice(-4)}` : '***';
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
        <span>{masked}</span>
        <EyeOutlined style={{ cursor: 'pointer', color: '#0958d9' }} onClick={handleViewCccd} />
      </span>
    );
  };

  if (!buyer_invoice_info) {
    return (
      <Card
        title="Thông tin xuất hóa đơn"
        size="small"
        extra={
          onEdit && <Button size="small" onClick={onEdit}>
            Sửa
          </Button>
        }
      >
        <div style={{ color: '#6b6b6b', textAlign: 'center', padding: '12px 0' }}>
          Chưa cấu hình xuất hóa đơn VAT
        </div>
      </Card>
    );
  }

  return (
    <Card
      title="Thông tin xuất hóa đơn"
      size="small"
      extra={
        onEdit && <Button size="small" onClick={onEdit}>
          Sửa
        </Button>
      }
    >
      <Descriptions column={1} size="small" bordered={false} labelStyle={{ color: '#666', width: 140 }}>
        <Descriptions.Item label="Tên khách hàng">
          <strong>{buyer_invoice_info.name || '---'}</strong>
        </Descriptions.Item>
        <Descriptions.Item label="MST/CCCD">
          {buyer_invoice_info.tax_id || '---'}
        </Descriptions.Item>
        <Descriptions.Item label="Địa chỉ xuất HĐ">
          {buyer_invoice_info.address || '---'}
        </Descriptions.Item>
        <Descriptions.Item label="Email nhận HĐ">
          {buyer_invoice_info.email || '---'}
        </Descriptions.Item>
        <Descriptions.Item label="Người mua hàng">
          {buyer_invoice_info.buyer_name || '---'}
        </Descriptions.Item>
        {buyer_invoice_info.buyer_cccd && (
          <Descriptions.Item label="CCCD người mua">
            {renderCccd()}
          </Descriptions.Item>
        )}
      </Descriptions>

      <Modal
        title="Xác thực bảo mật"
        open={isVerifyModalOpen}
        onOk={handleVerifyPassword}
        onCancel={() => {
          setIsVerifyModalOpen(false);
          setPassword('');
        }}
        okText="Xác nhận"
        cancelText="Hủy"
        confirmLoading={revealMutation.isPending}
        width={400}
      >
        <p style={{ marginBottom: 16 }}>Vui lòng nhập mật khẩu tài khoản để xem thông tin nhạy cảm.</p>
        <Input.Password
          placeholder="Mật khẩu"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          onPressEnter={handleVerifyPassword}
          autoFocus
        />
      </Modal>
    </Card>
  );
}
